import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { tokenService } from '../lib/tokenService';

interface TokenSyncLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout component that handles token synchronization across all pages
 * This component ensures the token is preserved in both URL and cookies
 * throughout the entire diagnostic journey
 */
const TokenSyncLayout: React.FC<TokenSyncLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialLoadRef = useRef(true);
  
  // Handle refresh and forward/back navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Skip token handling for admin routes and analyse routes
      const currentPath = location.pathname;
      if (currentPath.startsWith('/mon-espace') ||
          currentPath.includes('/login') ||
          currentPath.startsWith('/analyse/')) {
        return;
      }
      
      // Make sure token is saved to cookie before page refresh for public routes
      const urlToken = tokenService.getTokenFromUrl();
      if (urlToken) {
        tokenService.setToken(urlToken);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);
  
  // Handle URL synchronization on each route change
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip token handling completely for admin routes and analyse routes
    if (currentPath.startsWith('/mon-espace') ||
        currentPath.includes('/login') ||
        currentPath.startsWith('/analyse/')) {
      return;
    }
    
    // Synchronize token on public diagnostic pages only
    const activeToken = tokenService.synchronizeToken();
    
    // If we have a token but it's not in the URL, add it to the URL
    if (activeToken && !tokenService.getTokenFromUrl()) {
      const pathSegments = currentPath.split('/');
      const lastSegment = pathSegments[pathSegments.length - 1];
      
      // Check if the last segment is a token
      if (!/^[a-zA-Z0-9]{8}$/.test(lastSegment)) {
        const newPath = `${currentPath}/${activeToken}`;
        
        // Only do immediate navigation on initial page load to avoid double navigations
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
          navigate(newPath, { replace: true });
        } else {
          // For subsequent navigation, use timeout to prevent race conditions
          setTimeout(() => {
            if (location.pathname === currentPath) { // Check if we're still on the same path
              navigate(newPath, { replace: true });
            }
          }, 100);
        }
      }
    }
  }, [location.pathname, navigate]);

  return <>{children}</>;
};

export default TokenSyncLayout;
